import React, { useEffect } from "react";
import { GetServerSideProps, NextPage } from "next";
import Head from "next/head";
import { useSelector } from "react-redux";

import { Action, Category } from "constants/events";
import { MenuItem } from "custom-types/MenuItem";
import useDispensaries from "hooks/shop/useDispensaries";
import { useEventTracker } from "hooks/useEventTracker";
import useQueryParamState from "hooks/useQueryParamState";
import {
  getCoordinates,
  getIsValidStreetAddress,
} from "redux/selectors/location";

import Button from "components/botanic/Button";
import DeliveryMenu from "components/DeliveryGateway/DeliveryMenu";
import DeliveryMenuLoadingSkeletons from "components/DeliveryGateway/DeliveryMenu/DeliveryMenuLoadingSkeletons";
import DeliveryMenuCarousels from "components/DeliveryGateway/DeliveryMenuCarousels";
import HeaderV2 from "components/DeliveryGateway/HeaderV2";
import FulfillmentMode from "components/DeliveryGateway/types/FulfillmentMode";
import DispensaryFulfillmentInfo from "components/DispensaryFulfillmentInfo";
import XIcon from "components/Icons/x.svg";
import RecentlyViewedMenuItems from "components/Shared/RecentlyViewedMenuItems";

const ShopPage: NextPage = () => {
  const { publishEvent } = useEventTracker();

  const coordinates = useSelector(getCoordinates);
  const isValidAddress = useSelector(getIsValidStreetAddress);

  const [fulfillmentPreference, setFulfillmentPreference] = useQueryParamState(
    "fulfillmentPreference"
  );
  const [attemptedRetailerName, setAttemptedRetailerName] = useQueryParamState(
    "attemptedRetailerName"
  );
  const [dispensarySort, setDispensarySort] =
    useQueryParamState("dispensarySort");

  const {
    deliveryDispensaries,
    pickupDispensaries,
    loading: loadingDispensaries,
  } = useDispensaries();

  useEffect(() => {
    if (isValidAddress && typeof window != "undefined") {
      window.scrollTo(0, 0);
    }
  }, [isValidAddress]);

  // * Determine which fulfillment mode to use before querying menu items
  let fulfillmentMode: FulfillmentMode;
  if (fulfillmentPreference) {
    fulfillmentMode = fulfillmentPreference as FulfillmentMode;
  } else if (deliveryDispensaries.length > 0) {
    fulfillmentMode = "delivery";
  } else {
    fulfillmentMode = "pickup";
  }

  // * Used for carousels, and to see if we have empty nearby results.
  const availableDispensaries =
    fulfillmentMode === "delivery" ? deliveryDispensaries : pickupDispensaries;

  return (
    <>
      <Head>
        <title>Shop Weed</title>
        <meta
          name="description"
          content="Find the best weed with Leafly. Order weed for same-day delivery, find reviews & menus from the top dispensaries."
        />
        <meta name="robots" content="noindex" />
      </Head>

      <HeaderV2
        fulfillmentMode={fulfillmentMode}
        showUnavailableAlert={
          availableDispensaries.length === 0 && !loadingDispensaries
        }
      />

      {!!attemptedRetailerName && (
        <div className="container rounded bg-leafly-white mt-xl p-md">
          <div className="flex justify-between">
            <div className="text-sm font-bold">{attemptedRetailerName}</div>
            <button onClick={() => setAttemptedRetailerName()}>
              <XIcon width="16" height="16" className="cursor-pointer" />
            </button>
          </div>
          <div className="text-xs font-bold text-error">
            Delivery unavailable to your address
          </div>
        </div>
      )}

      <DeliveryMenu
        coordinates={coordinates}
        enablePickupMode={fulfillmentMode === "pickup"}
        isDeliveryAvailable={fulfillmentMode === "delivery"}
        renderPreFilteredView={() => {
          if (loadingDispensaries) {
            return <DeliveryMenuLoadingSkeletons />;
          }

          const newFulfillmentMode =
            fulfillmentMode === "delivery" ? "pickup" : "delivery";
          return availableDispensaries.length > 0 ? (
            // * Render dispensary-centric view, without CategoryChips
            <DeliveryMenuCarousels
              dispensaries={availableDispensaries}
              isDeliveryAvailable={fulfillmentMode === "delivery"}
              selectedSort={dispensarySort}
              onChangeSort={setDispensarySort}
            />
          ) : (
            <div className="mb-xxl">
              <div className="mb-lg">{`${
                fulfillmentMode === "delivery" ? "Delivery" : "Pickup"
              } is not available at your address.`}</div>
              <Button
                variant="primary"
                onClick={() => setFulfillmentPreference(newFulfillmentMode)}
              >
                {`shop ${newFulfillmentMode} instead`}
              </Button>
            </div>
          );
        }}
      />

      <RecentlyViewedMenuItems
        className="border-t border-light-grey pt-xxl mb-xxl"
        carouselClassName="container"
        onCardClick={(menuItem: MenuItem) =>
          publishEvent({
            action: "click",
            brandId: menuItem?.brand?.id,
            category: Category.deliveryGateFlow,
            dispensaryId: menuItem?.dispensaryId,
            label: "recently viewed menu item card",
            menuItemId: menuItem?.id,
            productId: menuItem?.product?.id,
          })
        }
        onCardImpression={(menuItem: MenuItem) =>
          publishEvent({
            action: Action.impression,
            brandId: menuItem?.brand?.id,
            category: Category.deliveryGateFlow,
            dispensaryId: menuItem.dispensaryId,
            label: "recently viewed menu item card",
            menuItemId: menuItem.id,
            nonInteractionEvent: true,
            productId: menuItem?.product?.id,
          })
        }
        menuItemProps={{
          renderFulfillmentInfo: (dispensary) =>
            fulfillmentMode === "delivery" && (
              <DispensaryFulfillmentInfo dispensary={dispensary} />
            ),
          showFulfillment: false,
        }}
      />
    </>
  );
};

// * This should remain empty, as Shop is solely for Searching and should
// * not be used for / require any SEO or SSR pre-loading.
export const getServerSideProps: GetServerSideProps = async () => ({
  props: {
    disableFixedHeader: true,
  },
});

export default ShopPage;
